import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { applicationBuilderAPI } from '../../api/applicationBuilderAPI';
import './EditorStyles.css';
import './PersonalStatementEditorStyles.css';

const PersonalStatementEditor = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [documentData, setDocumentData] = useState(location.state?.documentData || null);
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState({});
  const [loadingFeedback, setLoadingFeedback] = useState({});
  const [personalStatement, setPersonalStatement] = useState({});
  const [subject, setSubject] = useState('');
  const [oxbridgeDepth, setOxbridgeDepth] = useState(false);
  const [graphMeta, setGraphMeta] = useState({});

  useEffect(() => {
    if (!documentData) {
      navigate('/application-builder/form/personal_statement');
    } else {
      setPersonalStatement(documentData.personal_statement || {});
      setFeedback(documentData.feedback || {});
      setSubject(documentData.subject || '');
    }
  }, [documentData, navigate]);

  const requestFeedback = async (questionNum) => {
    const questionKey = `question_${questionNum}`;
    const content = personalStatement[questionKey] || '';
    
    if (!content.trim() || content.length < 50) {
      alert('Please write at least 50 characters before requesting feedback.');
      return;
    }

    setLoadingFeedback(prev => ({ ...prev, [questionNum]: true }));
    
    try {
      const names = {
        first_name: documentData?.first_name || '',
        last_name: documentData?.last_name || '',
      };
      if (!names.first_name && !names.last_name && documentData?.full_name) {
        const parts = String(documentData.full_name).trim().split(/\s+/);
        names.first_name = parts[0] || '';
        names.last_name = parts.slice(1).join(' ');
      }
      const response = await applicationBuilderAPI.getFeedback(
        questionNum,
        content,
        subject,
        true,
        {
          oxbridge: oxbridgeDepth,
          snapshot: {
            ...names,
            subject: subject || documentData?.subject || '',
            question_1: personalStatement.question_1 || '',
            question_2: personalStatement.question_2 || '',
            question_3: personalStatement.question_3 || '',
            feedback,
          },
        }
      );
      
      if (response.feedback) {
        setFeedback(prev => ({ ...prev, [questionKey]: response.feedback }));
      }
      setGraphMeta(prev => ({
        ...prev,
        [questionKey]: {
          band: response.isomorphism?.band,
          dbWrite: response.db_write?.status,
        },
      }));
    } catch (error) {
      console.error('Error getting feedback:', error);
      alert(error?.data?.error === 'save_failed'
        ? error.data.message
        : 'Failed to get feedback. Please try again.');
    } finally {
      setLoadingFeedback(prev => ({ ...prev, [questionNum]: false }));
    }
  };

  const handleContentUpdate = (questionKey, value) => {
    setPersonalStatement(prev => ({ ...prev, [questionKey]: value }));
    // Clear feedback for this question when content is updated
    setFeedback(prev => {
      const newFeedback = { ...prev };
      delete newFeedback[questionKey];
      return newFeedback;
    });
  };

  if (!documentData) {
    return <div>Loading...</div>;
  }

  return (
    <div className="editor-container">
      <div className="editor-wrapper">
        <div className="editor-header">
          <h1>Personal Statement Review</h1>
          <p>Review your answers and AI feedback</p>
        </div>

        <div className="editor-actions">
          <button onClick={() => navigate('/application-builder/form/personal_statement')} className="edit-button">
            Edit Form
          </button>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginLeft: '1rem' }}>
            <input
              type="checkbox"
              checked={oxbridgeDepth}
              onChange={(e) => setOxbridgeDepth(e.target.checked)}
            />
            Oxbridge / tutorial-style depth
          </label>
        </div>

        <div className="document-preview">
          <div className="preview-content personal-statement">
            <section className="statement-section">
              <div className="statement-header">
                <h2>Question 1: Motivation and Subject Interest</h2>
                <button
                  className="feedback-button"
                  onClick={() => requestFeedback(1)}
                  disabled={loadingFeedback[1]}
                >
                  {loadingFeedback[1] ? 'Getting Feedback...' : 'Get Feedback'}
                </button>
              </div>
              <div className="answer-box">
                <textarea
                  className="editable-answer"
                  value={personalStatement.question_1 || ''}
                  onChange={(e) => handleContentUpdate('question_1', e.target.value)}
                  rows={6}
                  placeholder="Enter your response here..."
                />
              </div>
              {feedback.question_1 && (
                <div className="feedback-box">
                  <h3>AI Feedback:</h3>
                  <pre className="ps-editor-feedback-pre">{feedback.question_1}</pre>
                  <button
                    className="feedback-button small"
                    onClick={() => requestFeedback(1)}
                    disabled={loadingFeedback[1]}
                  >
                    {loadingFeedback[1] ? 'Updating...' : 'Request New Feedback'}
                  </button>
                </div>
              )}
            </section>

            <section className="statement-section">
              <div className="statement-header">
                <h2>Question 2: Academic Preparation and Skills</h2>
                <button
                  className="feedback-button"
                  onClick={() => requestFeedback(2)}
                  disabled={loadingFeedback[2]}
                >
                  {loadingFeedback[2] ? 'Getting Feedback...' : 'Get Feedback'}
                </button>
              </div>
              <div className="answer-box">
                <textarea
                  className="editable-answer"
                  value={personalStatement.question_2 || ''}
                  onChange={(e) => handleContentUpdate('question_2', e.target.value)}
                  rows={6}
                  placeholder="Enter your response here..."
                />
              </div>
              {feedback.question_2 && (
                <div className="feedback-box">
                  <h3>AI Feedback:</h3>
                  <pre className="ps-editor-feedback-pre">{feedback.question_2}</pre>
                  <button
                    className="feedback-button small"
                    onClick={() => requestFeedback(2)}
                    disabled={loadingFeedback[2]}
                  >
                    {loadingFeedback[2] ? 'Updating...' : 'Request New Feedback'}
                  </button>
                </div>
              )}
            </section>

            <section className="statement-section">
              <div className="statement-header">
                <h2>Question 3: Extracurricular Experiences and Personal Development</h2>
                <button
                  className="feedback-button"
                  onClick={() => requestFeedback(3)}
                  disabled={loadingFeedback[3]}
                >
                  {loadingFeedback[3] ? 'Getting Feedback...' : 'Get Feedback'}
                </button>
              </div>
              <div className="answer-box">
                <textarea
                  className="editable-answer"
                  value={personalStatement.question_3 || ''}
                  onChange={(e) => handleContentUpdate('question_3', e.target.value)}
                  rows={6}
                  placeholder="Enter your response here..."
                />
              </div>
              {feedback.question_3 && (
                <div className="feedback-box">
                  <h3>AI Feedback:</h3>
                  <pre className="ps-editor-feedback-pre">{feedback.question_3}</pre>
                  <button
                    className="feedback-button small"
                    onClick={() => requestFeedback(3)}
                    disabled={loadingFeedback[3]}
                  >
                    {loadingFeedback[3] ? 'Updating...' : 'Request New Feedback'}
                  </button>
                </div>
              )}
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonalStatementEditor;

